import type {
  HoldOutcome,
  LedgerPerson,
  OperationEntry,
  OperationPosting,
  ReleaseReason,
} from "@pax/api-contract/accounting/v1alpha1";
import { Check, Copy } from "lucide-react";
import {
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { formatRawCredits } from "@/features/credits/format";
import { journalLabel } from "@/features/credits/journals";
import { button, link, Tabs } from "@/features/evals/components";
import {
  bucketLabels,
  exactMoment,
  localMoment,
  outcomeLabels,
  systemAccountLabel,
  timeAgo,
  utcMoment,
} from "./format";

function outcomeTone(outcome: HoldOutcome | ReleaseReason) {
  if (outcome === "settled") return "bg-success/10 text-success";
  if (outcome === "open") return "bg-foreground/5 text-muted-foreground";
  return "bg-foreground/10 text-foreground/70";
}

/**
 * How a hold ended, as a small tag: settled green, released with nothing charged gray, and open
 * plain.
 */
export function OutcomeTag({ outcome }: { outcome: HoldOutcome | ReleaseReason }) {
  return (
    <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${outcomeTone(outcome)}`}>
      {outcomeLabels[outcome]}
    </span>
  );
}

/** A hold nobody was charged for, and what it had reserved when that is known. */
export function NotCharged({ reservedRaw }: { reservedRaw: string | null }) {
  return (
    <span className="text-muted-foreground">
      Not charged
      {reservedRaw && (
        <>
          {" "}
          (<Credits raw={reservedRaw} /> reserved)
        </>
      )}
    </span>
  );
}

/** Shows every row on its own instead of folding back-to-back alike holds together. */
export function ExpandAllToggle({
  expandAll,
  onChange,
}: {
  expandAll: boolean;
  onChange: (expandAll: boolean) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={expandAll}
      className={`rounded-full px-2.5 py-0.5 text-xs ${expandAll ? "bg-foreground/10 font-medium" : "bg-foreground/5 text-muted-foreground"}`}
      onClick={() => onChange(!expandAll)}
    >
      Expand all
    </button>
  );
}

/** An exact amount of raw credits, shown as display credits, with the raw count on hover. */
export function Credits({
  raw,
  signed = false,
  toned = false,
}: {
  raw: string;
  signed?: boolean;
  /** Gains green and losses red. */
  toned?: boolean;
}) {
  const tone =
    !toned || raw === "0" ? "" : raw.startsWith("-") ? "text-destructive" : "text-success";
  return (
    <span title={`${BigInt(raw).toLocaleString()} raw`} className={`tabular-nums ${tone}`}>
      {formatRawCredits(raw, { signed })}
    </span>
  );
}

/** Where a person's or a game's name leads inside the ledger. Without it, names are plain text. */
export interface LedgerLinks {
  openPlayer: (userId: string) => void;
  openGame: (gameId: string) => void;
}

export const LedgerLinksContext = createContext<LedgerLinks | null>(null);

const COPIED_FOR_MS = 1500;

/** Copies an id, and says so for a moment. */
export function CopyId({ id, label }: { id: string; label: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FOR_MS);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      aria-label={copied ? "Copied" : `Copy ${label}`}
      title={copied ? "Copied" : `Copy ${label} ${id}`}
      onClick={() => void navigator.clipboard.writeText(id).then(() => setCopied(true))}
      className="inline-flex shrink-0 items-center gap-1 self-center rounded text-xs text-muted-foreground hover:text-foreground"
    >
      {copied ? (
        <>
          <Check aria-hidden className="size-3" />
          Copied
        </>
      ) : (
        <Copy aria-hidden className="size-3" />
      )}
    </button>
  );
}

/** A name that opens its page in the ledger when there is one, with its id on hover and to copy. */
function LinkedName({
  id,
  idLabel,
  open,
  children,
}: {
  id: string;
  idLabel: string;
  open: ((id: string) => void) | undefined;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex max-w-full items-baseline gap-1.5" title={id}>
      {open ? (
        <button type="button" className={`text-left ${link}`} onClick={() => open(id)}>
          {children}
        </button>
      ) : (
        <span>{children}</span>
      )}
      <CopyId id={id} label={idLabel} />
    </span>
  );
}

/** A person by name and handle, opening their history in the ledger. */
export function PersonName({ person }: { person: LedgerPerson }) {
  const links = useContext(LedgerLinksContext);
  return (
    <LinkedName id={person.userId} idLabel="user id" open={links?.openPlayer}>
      {person.displayName}
      {person.username && <span className="text-muted-foreground"> @{person.username}</span>}
    </LinkedName>
  );
}

/** A game by title, opening it in the ledger. */
export function GameName({ gameId, title }: { gameId: string; title: string | null | undefined }) {
  const links = useContext(LedgerLinksContext);
  return (
    <LinkedName id={gameId} idLabel="game id" open={links?.openGame}>
      {title ?? "Deleted game"}
    </LinkedName>
  );
}

/** Whether the ledger's times read as how long ago, and the switch every time on the page shares. */
export const LedgerTimesContext = createContext<{
  ago: boolean;
  toggle: () => void;
} | null>(null);

/** The time now, to the last whole `ms`, telling its readers when that moves on. */
function clock(ms: number) {
  const listeners = new Set<() => void>();
  let timer: ReturnType<typeof setInterval> | undefined;
  return {
    now: () => Math.floor(Date.now() / ms) * ms,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      timer ??= setInterval(() => {
        for (const notify of listeners) notify();
      }, ms);
      return () => {
        listeners.delete(listener);
        if (listeners.size > 0) return;
        clearInterval(timer);
        timer = undefined;
      };
    },
  };
}

const everySecond = clock(1000);
const everyMinute = clock(60_000);
const unsubscribed = () => () => {};

/**
 * An instant in the reader's own time, to the second, or how long ago it was, with the other on
 * hover. Pressing it switches every time on the page. How long ago counts seconds for the first
 * two minutes, then moves on each minute.
 */
export function When({ iso }: { iso: string }) {
  const times = useContext(LedgerTimesContext);
  const ago = times?.ago === true;
  const minute = useSyncExternalStore(ago ? everyMinute.subscribe : unsubscribed, everyMinute.now);
  const recent = ago && minute - Date.parse(iso) < 120_000;
  const second = useSyncExternalStore(
    recent ? everySecond.subscribe : unsubscribed,
    everySecond.now,
  );
  const since = timeAgo(iso, Math.max(minute, second));
  const time = (
    <time dateTime={iso} className="whitespace-nowrap tabular-nums">
      {ago ? since : exactMoment(iso)}
    </time>
  );
  if (!times) return <span title={since}>{time}</span>;
  return (
    // biome-ignore lint/a11y/useSemanticElements: a button can't sit inside the row's own button
    <span
      role="button"
      tabIndex={0}
      title={ago ? exactMoment(iso) : since}
      className="cursor-pointer rounded underline decoration-foreground/25 decoration-dotted underline-offset-2 hover:decoration-foreground/60"
      onClick={event => {
        event.stopPropagation();
        times.toggle();
      }}
      onKeyDown={event => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        times.toggle();
      }}
    >
      {time}
    </span>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return <p className="py-2 text-sm text-muted-foreground">{children}</p>;
}

/** A row whose time falls in the span picked on a graph. */
export const pickedRow = "[&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-foreground/30";

/** The latest rolled-up hour in the reader's own time, with the exact UTC time on hover. */
export function DataThrough({ to }: { to: string }) {
  return (
    <p className="text-xs text-muted-foreground" title={utcMoment(to)}>
      Data through {localMoment(to)}
    </p>
  );
}

export interface TableColumn {
  label: string;
  numeric?: boolean;
}

export function Table({
  columns,
  rows,
}: {
  columns: readonly TableColumn[];
  rows: readonly { key: string; cells: readonly ReactNode[] }[];
}) {
  const cellClass = (index: number) =>
    `py-2 pr-4 last:pr-0 ${columns[index]?.numeric ? "whitespace-nowrap text-right tabular-nums" : "text-left"}`;
  return (
    <div className="-mx-4 overflow-x-auto px-4">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={column.label}
                scope="col"
                className={`${cellClass(index)} pt-0 text-xs font-normal text-muted-foreground`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.key} className="border-t border-foreground/[0.07]">
              {row.cells.map((cell, index) => (
                <td key={columns[index]?.label ?? index} className={cellClass(index)}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const PAGE_SIZES = [25, 50, 100, 250, 500] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 100;

export function PageSizePicker({
  pageSize,
  onChange,
}: {
  pageSize: PageSize;
  onChange: (pageSize: PageSize) => void;
}) {
  return (
    <Tabs
      compact
      value={String(pageSize)}
      options={PAGE_SIZES.map(size => ({ value: String(size), label: `${size} per page` }))}
      onChange={size => onChange(Number(size) as PageSize)}
    />
  );
}

/**
 * Newest, Newer, what the page shows, and Older. Each page is its own location, so Back returns to
 * the one before. With `scrollTo`, turning a page brings the list's top back into view when it has
 * scrolled above the window, and leaves the scroll alone otherwise.
 */
export function Pager({
  label,
  loading,
  toNewest,
  toNewer,
  toOlder,
  newer = true,
  scrollTo,
}: {
  /** What the page shows, in place of a page number. */
  label: string;
  loading: boolean;
  /** Missing on the newest page. */
  toNewest?: () => void;
  toNewer?: () => void;
  /** Missing once nothing is older. */
  toOlder?: () => void;
  /** Whether the list can step one page newer at all. Without it, Back is the way. */
  newer?: boolean;
  scrollTo?: RefObject<HTMLElement | null>;
}) {
  if (!toNewest && !toOlder) return null;
  const turn = (to: (() => void) | undefined) => {
    to?.();
    const top = scrollTo?.current;
    if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ block: "start" });
  };
  return (
    <div className={`${scrollTo ? "mt-4" : "mb-2"} flex flex-wrap items-center gap-2`}>
      <button
        type="button"
        className={button}
        disabled={!toNewest || loading}
        onClick={() => turn(toNewest)}
      >
        Newest
      </button>
      {newer && (
        <button
          type="button"
          className={button}
          disabled={!toNewer || loading}
          onClick={() => turn(toNewer)}
        >
          Newer
        </button>
      )}
      <span className="px-2 text-sm tabular-nums text-muted-foreground">
        {loading ? "Loading…" : label}
      </span>
      <button
        type="button"
        className={button}
        disabled={!toOlder || loading}
        onClick={() => turn(toOlder)}
      >
        Older
      </button>
    </div>
  );
}

export const presetName = (title: string | null | undefined) => title ?? "Deleted preset";

/** What an entry moved: its debits, which its credits match. */
function entryTotal(entry: OperationEntry) {
  return entry.postings
    .filter(posting => posting.side === "debit")
    .reduce((total, posting) => total + BigInt(posting.amountRaw), 0n)
    .toString();
}

export function EntryTable({ entry }: { entry: OperationEntry }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{journalLabel(entry.journalCode)}</span>
        {entry.recipient && (
          <span className="text-foreground">
            {" "}
            <Credits raw={entryTotal(entry)} /> → <PersonName person={entry.recipient} />
          </span>
        )}{" "}
        · entry {entry.entryId}
        {entry.presetId && ` · ${presetName(entry.presetTitle)}`}
      </p>
      <Table
        columns={[
          { label: "Account" },
          { label: "Debit", numeric: true },
          { label: "Credit", numeric: true },
        ]}
        rows={entry.postings.map(posting => ({
          key: String(posting.legIndex),
          cells: [
            accountLabel(posting, entry.recipient),
            posting.side === "debit" ? <Credits key="debit" raw={posting.amountRaw} /> : "",
            posting.side === "credit" ? <Credits key="credit" raw={posting.amountRaw} /> : "",
          ],
        }))}
      />
    </div>
  );
}

function accountLabel(posting: OperationPosting, recipient: OperationEntry["recipient"]) {
  if (!posting.bucket) {
    const account = systemAccountLabel(posting.accountCode);
    return posting.accountCode === "creator_rewards_pending" && recipient ? (
      <span key="account">
        {account} for <PersonName person={recipient} />
      </span>
    ) : (
      account
    );
  }
  const bucket = bucketLabels[posting.bucket];
  return posting.accountUser ? (
    <span key="account">
      <PersonName person={posting.accountUser} /> · {bucket}
    </span>
  ) : (
    `Player · ${bucket}`
  );
}
